if (!customElements.get("featured-product-variant-selector")) {
  class FeaturedProductVariantSelector extends HTMLElement {
    constructor() {
      super();
      this.handleChange = this.onVariantChange.bind(this);
      this.variantData = null;
      this.currentVariant = null;
      this.options = [];
    }

    connectedCallback() {
      this.section = this.closest("[data-featured-product-section]");
      this.form = document.getElementById(this.dataset.formId);
      this.variantInput = this.form?.querySelector("[data-featured-product-variant-input]");

      this.addEventListener("change", this.handleChange);
      this.initializeState();
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.handleChange);
    }

    initializeState() {
      this.updateOptions();
      this.updateMasterId();

      if (!this.currentVariant) {
        const variants = this.getVariantData();
        this.currentVariant =
          variants.find((variant) => String(variant.id) === String(this.variantInput?.value)) ||
          variants[0] ||
          null;
      }

      if (!this.currentVariant) return;

      this.updateVariantInput();
      this.updatePrice();
      this.updateAvailability();
      this.updateImage();
      this.syncQuantityBounds();
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();

      if (!this.currentVariant) return;

      this.updateVariantInput();
      this.updatePrice();
      this.updateAvailability();
      this.updateImage();
      this.syncQuantityBounds();
      this.dispatchVariantUpdatedEvent();
    }

    updateOptions() {
      const optionContainers = this.querySelectorAll(".product-form__input");

      this.options = Array.from(optionContainers, (container) => {
        const radioInput = container.querySelector('input[type="radio"]:checked');
        if (radioInput) return radioInput.value;

        const selectInput = container.querySelector("select");
        if (selectInput) return selectInput.value;

        return null;
      }).filter((value) => value !== null);
    }

    updateMasterId() {
      const variants = this.getVariantData();
      if (variants.length === 0) {
        this.currentVariant = null;
        return;
      }

      if (this.options.length === 0) {
        this.currentVariant =
          variants.find((variant) => String(variant.id) === String(this.variantInput?.value)) || variants[0];
        return;
      }

      this.currentVariant = variants.find((variant) => {
        return !variant.options
          .map((option, index) => {
            return this.options[index] === option;
          })
          .includes(false);
      });
    }

    updateVariantInput() {
      if (!this.variantInput || !this.currentVariant) return;
      this.variantInput.value = this.currentVariant.id;
      this.variantInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    updateImage() {
      if (!this.section || !this.currentVariant) return;

      const imageElement = this.section.querySelector("[data-featured-product-image]");
      if (!imageElement) return;

      const nextSrc = this.currentVariant.featured_media_src || imageElement.dataset.featuredProductFallbackSrc;
      if (!nextSrc) return;

      imageElement.src = nextSrc;
      imageElement.removeAttribute("srcset");
    }

    updatePrice() {
      if (!this.section || !this.currentVariant) return;

      const priceHost = this.section.querySelector("[data-featured-product-price-host]");
      const priceContainer = priceHost?.querySelector(".pdp-price");
      if (!priceContainer) return;

      while (priceContainer.firstChild) {
        priceContainer.removeChild(priceContainer.firstChild);
      }

      const currentPrice = document.createElement("span");
      currentPrice.className = "pdp-original-price";
      currentPrice.textContent = this.currentVariant.price_money;

      if (
        this.currentVariant.compare_at_price &&
        this.currentVariant.compare_at_price > this.currentVariant.price &&
        this.currentVariant.compare_at_price_money
      ) {
        currentPrice.style.color = "var(--color-price-foreground)";
        priceContainer.appendChild(currentPrice);

        const comparePrice = document.createElement("s");
        comparePrice.className = "pdp-price-compare-at-price";
        comparePrice.textContent = this.currentVariant.compare_at_price_money;
        priceContainer.appendChild(comparePrice);

        const savings = this.currentVariant.compare_at_price - this.currentVariant.price;
        const discount = Math.round((savings * 100) / this.currentVariant.compare_at_price);

        const discountBadge = document.createElement("span");
        discountBadge.className = "pdp-price-saving-badge";
        discountBadge.textContent = `${discount}% Off`;
        priceContainer.appendChild(discountBadge);
      } else {
        priceContainer.appendChild(currentPrice);
      }
    }

    updateAvailability() {
      if (!this.section || !this.currentVariant) return;

      const submitButton = this.section.querySelector(`add-to-cart button[form="${this.dataset.formId}"]`);
      if (!submitButton) return;

      const buttonText = submitButton.querySelector(".atc-button__text");
      const addLabel = submitButton.dataset.addLabel || "ADD TO CART";
      const soldOutLabel = submitButton.dataset.soldOutLabel || "SOLD OUT";

      submitButton.disabled = !this.currentVariant.available;

      if (buttonText) {
        buttonText.textContent = this.currentVariant.available ? addLabel : soldOutLabel;
      }

      submitButton.setAttribute(
        "aria-label",
        this.currentVariant.available ? addLabel : soldOutLabel,
      );

      const dynamicCheckout = this.section.querySelector(".pdp-atc-dynamic-checkout-button");
      if (dynamicCheckout) {
        dynamicCheckout.style.display = this.currentVariant.available ? "" : "none";
      }
    }

    syncQuantityBounds() {
      if (!this.section || !this.currentVariant) return;

      const quantityElement = this.section.querySelector("featured-product-quantity-selector");
      if (!quantityElement || typeof quantityElement.setVariantBounds !== "function") return;

      quantityElement.setVariantBounds(this.currentVariant);
    }

    dispatchVariantUpdatedEvent() {
      if (!this.currentVariant) return;

      document.dispatchEvent(
        new CustomEvent("featured-product:variant-updated", {
          bubbles: true,
          detail: {
            sectionId: this.dataset.sectionId,
            variantId: this.currentVariant.id,
            variant: this.currentVariant,
          },
        }),
      );
    }

    getVariantData() {
      if (this.variantData) return this.variantData;

      const variantScript = this.querySelector('[type="application/json"]');
      if (!variantScript) {
        this.variantData = [];
        return this.variantData;
      }

      this.variantData = JSON.parse(variantScript.textContent);
      return this.variantData;
    }
  }

  customElements.define("featured-product-variant-selector", FeaturedProductVariantSelector);
}

if (!customElements.get("featured-product-quantity-selector")) {
  class FeaturedProductQuantitySelector extends HTMLElement {
    constructor() {
      super();
      this.handleMinusClick = this.handleMinusClick.bind(this);
      this.handlePlusClick = this.handlePlusClick.bind(this);
      this.handleInputChange = this.handleInputChange.bind(this);
    }

    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.hiddenQuantityInput = this.form?.querySelector("[data-featured-product-quantity-input]");
      this.displayInput = this.querySelector("[data-quantity-display]");
      this.minusButton = this.querySelector("[data-quantity-minus]");
      this.plusButton = this.querySelector("[data-quantity-plus]");

      if (!this.displayInput || !this.minusButton || !this.plusButton) return;

      this.syncBoundsFromDataset();
      this.syncToHiddenInput();

      this.minusButton.addEventListener("click", this.handleMinusClick);
      this.plusButton.addEventListener("click", this.handlePlusClick);
      this.displayInput.addEventListener("input", this.handleInputChange);
    }

    disconnectedCallback() {
      if (this.minusButton) {
        this.minusButton.removeEventListener("click", this.handleMinusClick);
      }

      if (this.plusButton) {
        this.plusButton.removeEventListener("click", this.handlePlusClick);
      }

      if (this.displayInput) {
        this.displayInput.removeEventListener("input", this.handleInputChange);
      }
    }

    setVariantBounds(variant) {
      const trackInventory = Boolean(variant.inventory_management) && variant.inventory_policy !== "continue";

      this.dataset.trackInventory = trackInventory ? "true" : "false";
      this.dataset.max = trackInventory ? String(Math.max(variant.inventory_quantity || 0, 0)) : "";

      this.syncBoundsFromDataset();
      this.syncToHiddenInput();
    }

    syncBoundsFromDataset() {
      this.trackInventory = this.dataset.trackInventory === "true";

      const parsedMax = Number.parseInt(this.dataset.max, 10);
      this.maxQuantity = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : null;

      const nextValue = this.clampValue(Number.parseInt(this.displayInput?.value, 10));
      if (this.displayInput) {
        this.displayInput.value = nextValue;
      }

      this.updateButtonState();
    }

    clampValue(value) {
      let nextValue = Number.isFinite(value) ? value : 1;
      if (nextValue < 1) nextValue = 1;

      if (this.trackInventory && this.maxQuantity !== null) {
        nextValue = Math.min(nextValue, this.maxQuantity);
      }

      return nextValue;
    }

    updateButtonState() {
      if (!this.displayInput || !this.minusButton || !this.plusButton) return;

      const value = Number.parseInt(this.displayInput.value, 10) || 1;

      this.minusButton.disabled = value <= 1;
      this.plusButton.disabled = Boolean(this.trackInventory && this.maxQuantity !== null && value >= this.maxQuantity);
    }

    syncToHiddenInput() {
      if (!this.hiddenQuantityInput || !this.displayInput) return;
      this.hiddenQuantityInput.value = this.displayInput.value;
    }

    handleMinusClick() {
      if (!this.displayInput) return;

      const current = Number.parseInt(this.displayInput.value, 10) || 1;
      const nextValue = this.clampValue(current - 1);

      this.displayInput.value = nextValue;
      this.syncToHiddenInput();
      this.updateButtonState();
    }

    handlePlusClick() {
      if (!this.displayInput) return;

      const current = Number.parseInt(this.displayInput.value, 10) || 1;
      const nextValue = this.clampValue(current + 1);

      this.displayInput.value = nextValue;
      this.syncToHiddenInput();
      this.updateButtonState();
    }

    handleInputChange() {
      if (!this.displayInput) return;

      const entered = Number.parseInt(this.displayInput.value, 10);
      const nextValue = this.clampValue(entered);

      this.displayInput.value = nextValue;
      this.syncToHiddenInput();
      this.updateButtonState();
    }
  }

  customElements.define("featured-product-quantity-selector", FeaturedProductQuantitySelector);
}
